<template>
  <div
    v-if="comparisonGraph"
    :id="comparisonGraph.id"
    class="py-6 px-6 flex-grow bg-pure-white rounded-xl"
  >
    <!-- :style="{ height: '21rem' }" -->
    <div class="mb-5 flex gap-2">
      <div
        class="handle_comparison h-7.5 w-5 mr-1 flex items-center justify-center cursor-grab"
      >
        <BlIcon icon="drag" color="slate-6" class="w-5 h-5" />
      </div>
      <div class="flex flex-wrap gap-2">
        <BlInputSelect
          v-if="enableBranch"
          :input="branchInput"
          @changed="onChangeHandler"
        />
        <BlInputSelect
          :input="calculationMethodInput"
          @changed="onChangeHandler"
        />
        <BlInputSelect
          v-if="useStage"
          :input="stageInput"
          @changed="onChangeHandler"
        />
        <BlInputSelect
          v-if="useGroupBy"
          :input="groupByInput"
          @changed="onChangeHandler"
        />
        <BlInputFilter
          :activeLabel="dateActiveLabel"
          title="Date"
          :defaultValue="[defaultDate]"
          :input="dateInput"
          @changed="onChangeDates"
        />
      </div>
      <BlInputButton
        class="ml-auto"
        type="plain-error"
        dense
        icon="delete"
        title="Delete comparison graph"
        @clicked="deleteOpened = true"
      />
    </div>
    <div v-if="fetchLoading" class="h-64 grid place-content-center">
      <div class="blum-loader"></div>
    </div>
    <div v-else class="relative flex flex-col gap-5">
      <div
        v-if="calculationMethodInput.model.value === 'prospects_per_stage'"
        class="px-8 flex items-center gap-2"
      >
        <BlTextBody2 text="Total:" class="text-slate-600" />
        <BlTextTitle4 :text="totalProspect" class="text-slate-900" />
        <BlTextBody2 text="prospect(s)" class="text-slate-600" />
      </div>
      <div v-else class="px-8">
        <BlTextBody2 text="Total final outcomes made during timeframe:" />
        <div class="flex items-center gap-2">
          <div class="flex items-center gap-2">
            <BlTextTitle4 :text="totalAdmitted" class="text-slate-900" />
            <BlTextBody2 text="Admitted" class="text-slate-600" />
          </div>
          <div
            v-if="totalDiscontinued !== null"
            class="flex items-center gap-2"
          >
            <div class="h-8 w-0.5 rounded bg-slate-300"></div>
            <BlTextTitle4 :text="totalDiscontinued" class="text-slate-900" />
            <BlTextBody2 text="Discontinued" class="text-slate-600" />
          </div>
        </div>
      </div>
      <BarChart
        v-if="calculationMethodInput.model.value === 'prospects_per_stage'"
        :labels="barChartDataProspect.labels"
        :datasets="barChartDataProspect.datasets"
        stacked
        height="12.5rem"
        width="calc((100vw - 15rem - 3.5rem) - 3rem)"
      />
      <BarChart
        v-if="calculationMethodInput.model.value === 'final_outcomes'"
        :labels="barChartDataFinal.labels"
        :datasets="barChartDataFinal.datasets"
        stacked
        height="12.5rem"
        width="calc((100vw - 15rem - 3.5rem) - 3rem)"
      />
      <BarChart
        v-if="calculationMethodInput.model.value === 'conversion'"
        :labels="barChartDataConversion.labels"
        :datasets="barChartDataConversion.datasets"
        :percentages="barChartDataConversion.percentages"
        stacked
        height="12.5rem"
        width="calc((100vw - 15rem - 3.5rem) - 3rem)"
        :oneBar="128"
      />
    </div>
    <BlBackdrop
      v-if="deleteOpened"
      title="Delete comparison graph"
      label="Delete"
      description="You are about to delete comparison graph. Are you sure you want to proceed? This action is irreversible."
      type="error"
      empty
      :disabled="deleteLoading"
      @backdrop-close="deleteOpened = false"
      @backdrop-action="onRemove"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";

import BarChart from "../graphs/BarChart.vue";

import BlTextTitle4 from "../../BlTextTitle4.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlIcon from "../../BlIcon.vue";
import BlInputSelect from "../../BlInputSelect.vue";
import BlInputFilter from "../../BlInputFilter.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlInputPagination from "../../BlInputPagination.vue";
import BlBackdrop from "../../BlBackdrop.vue";

export default {
  name: "ComparisonAcquisitionGraph",
  components: {
    BarChart,
    BlTextTitle4,
    BlTextSubtitle3,
    BlTextBody2,
    BlIcon,
    BlInputSelect,
    BlInputFilter,
    BlInputButton,
    BlInputPagination,
    BlBackdrop
  },
  props: {
    comparisonGraph: { type: Object, required: true }
  },
  data() {
    const interval = "year";
    const defaultDate = {
      start_date: moment()
        .startOf(interval)
        .format("YYYY-MM-DD"),
      end_date: moment()
        .endOf(interval)
        .format("YYYY-MM-DD")
    };
    return {
      defaultDate,
      /** */
      fetchLoading: false,
      graphData: null,
      /** */
      branchInput: {
        model: { value: "all", name: "All branches" },
        prefix: "Branch",
        combine: true,
        dense: true,
        options: []
      },
      calculationMethodInput: {
        model: { value: "prospects_per_stage", name: "Prospects per stage" },
        prefix: "Calculation method",
        combine: true,
        dense: true,
        options: [
          { value: "prospects_per_stage", name: "Prospects per stage" },
          { value: "final_outcomes", name: "Final outcomes" },
          { value: "conversion", name: "Conversion" }
        ]
      },
      stageInput: {
        model: {
          value: "",
          name: "Stage",
          is_discontinued: false,
          is_admitted: false
        },
        prefix: "Stage",
        combine: true,
        dense: true,
        options: []
      },
      groupByInput: {
        model: { value: "month", name: "Month" },
        prefix: "Group by",
        combine: true,
        dense: true,
        options: [
          { name: "Day", value: "day", disabled: false },
          { name: "Week", value: "week", disabled: false },
          { name: "Month", value: "month" },
          { name: "Year", value: "year" }
        ]
      },
      dateInput: {
        model: [defaultDate],
        tabs: [{ name: "Date", kind: "date" }],
        dense: true
      },
      /** */
      totalProspect: 0,
      totalAdmitted: 0,
      totalDiscontinued: 0,
      /** */
      deleteOpened: false,
      deleteLoading: false
    };
  },
  computed: {
    ...mapGetters(["currentUser", "currentCenter", "branches", "stages"]),
    enableBranch() {
      return this.currentCenter.enable_branch;
    },
    useStage() {
      return this.calculationMethodInput.model.value === "prospects_per_stage";
    },
    useGroupBy() {
      return (
        this.calculationMethodInput.model.value === "prospects_per_stage" ||
        this.calculationMethodInput.model.value === "final_outcomes"
      );
    },
    dateActiveLabel() {
      const start = this.dateInput.model[0].start_date;
      const end = this.dateInput.model[0].end_date;

      const sYYYY = moment(start).format("YYYY");
      const sMMM = moment(start).format("MMM");
      const sDD = moment(start).format("DD");

      const eYYYY = moment(end).format("YYYY");
      const eMMM = moment(end).format("MMM");
      const eDD = moment(end).format("DD");

      let date = "";
      if (sYYYY === eYYYY) {
        if (sMMM === eMMM) date = `${sDD} - ${eDD} ${sMMM}, ${sYYYY}`;
        else if (sMMM === "Jan" && eMMM === "Dec") date = `${sYYYY}`;
        else date = `${sDD} ${sMMM} - ${eDD} ${eMMM}, ${sYYYY}`;
      } else date = `${sYYYY} - ${eYYYY}`;
      return `
        <span class="flex items-center gap-2">
          <span>Date</span>
          <b>${date}</b>
        </span>
      `;
    },
    /*
    {
      "date": "2024-01-01",
      "total": 0
    },
    */
    barChartDataProspect() {
      const n = { labels: [], datasets: [], empty: true };
      const raw = this.graphData;
      if (!raw) return n;
      const keys = ["total"];
      let colors = ["#0095D1"];
      if (this.stageInput.model.is_discontinued) {
        colors = ["#F63D68"]; // rose-7
      }
      if (this.stageInput.model.is_pending) {
        colors = ["#E8B100"]; // tulip-7
      }
      if (this.stageInput.model.is_admitted) {
        colors = ["#B9D843"]; // lime-5
      }
      const texts = this.stageInput.model.name;

      const labels = [];
      const datasets = keys.map((key, idx) => ({
        label: texts,
        data: [],
        color: colors[idx]
      }));
      let empty = true;

      const formats = {
        day: "DD/MM/YY",
        week: "DD/MM",
        month: "MMM YYYY",
        year: "YYYY"
      };
      const groupBy = this.groupByInput.model.value;

      raw.forEach(i => {
        let l = moment(i.date)
          .format(formats[groupBy])
          .toUpperCase();
        if (groupBy === "week") {
          l += `-${moment(i.date)
            .add(6, "days")
            .format(formats[groupBy])}`;
        }
        labels.push(l);
        keys.forEach((key, idx) => {
          const value = i[key] || 0;
          datasets[idx].data.push(value);
          if (value) empty = false;
        });
      });

      return { labels, datasets, empty };
    },
    /*
    {
      "date": "2024-01-01",
      "total_admitted": 0,
      "total_discontinued": 0
    },
    */
    barChartDataFinal() {
      const n = { labels: [], datasets: [], empty: true };
      const raw = this.graphData;
      if (!raw) return n;
      const keys = ["total_admitted", "total_discontinued"];
      const colors = ["#B9D843", "#F63D68"];

      const stages = [...this.stages] || [];
      const discontinued = stages.find(i => i.is_discontinued) || {
        name: "Discontinued"
      };
      const admitted = stages.find(i => i.is_admitted) || { name: "Admitted" };
      const texts = [admitted.name, discontinued.name];

      const labels = [];
      const datasets = keys.map((key, idx) => ({
        label: texts[idx],
        data: [],
        color: colors[idx]
      }));
      let empty = true;

      const formats = {
        day: "DD/MM/YY",
        week: "DD/MM",
        month: "MMM YYYY",
        year: "YYYY"
      };
      const groupBy = this.groupByInput.model.value;

      raw.forEach(i => {
        let l = moment(i.date)
          .format(formats[groupBy])
          .toUpperCase();
        if (groupBy === "week") {
          l += `-${moment(i.date)
            .add(6, "days")
            .format(formats[groupBy])}`;
        }
        labels.push(l);
        keys.forEach((key, idx) => {
          const value = i[key] || 0;
          datasets[idx].data.push(value);
          if (value) empty = false;
        });
      });

      return { labels, datasets, empty };
    },
    /*
    [
      { "stage_name": "Inquiry", "total": 18, "percentage": null },
      { "stage_name": "Consultation HEHE", "total": 14, "percentage": 78 },
      { "stage_name": "Assessment", "total": 10, "percentage": 56 },
      { "stage_name": "Admitted", "total": 9, "percentage": 50 }
    ]
    */
    barChartDataConversion() {
      const n = { labels: [], datasets: [], percentages: [], empty: true };
      let raw = this.graphData;
      if (!raw) return n;
      raw = raw.map(i => {
        return {
          label: i.stage_name,
          total: i.stage_name === "Admitted" ? i.total : 0,
          total_admitted: i.stage_name === "Admitted" ? 0 : i.total,
          percentage: i.percentage
        };
      });
      const keys = ["total", "total_admitted"];
      const colors = ["#B9D843", "#0095D1"];

      const labels = [];
      const datasets = keys.map((key, idx) => ({
        data: [],
        color: colors[idx]
      }));
      const percentages = [];
      let empty = true;

      raw.forEach(i => {
        labels.push(i.label);
        keys.forEach((key, idx) => {
          const value = i[key] || 0;
          datasets[idx].data.push(value);
          if (value) empty = false;
        });
        percentages.push(i.percentage);
      });

      return {
        labels,
        datasets,
        percentages: percentages.filter(i => i !== null),
        empty
      };
    }
  },
  watch: {
    branches: {
      deep: true,
      immediate: true,
      handler(val) {
        const allBranch = {
          name: "All branches",
          value: "all"
        };
        const branchOptions = val.map(i => ({
          name: i.name,
          value: i.id,
          color: i.color,
          backgroundColor: i.background_color
        }));
        const noBranch = {
          name: "No branch",
          value: "no_branch"
        };
        const arr = [allBranch, ...branchOptions, noBranch];
        this.branchInput.options = arr;
        /** */
        const filter = this.comparisonGraph.filter_data;
        if (filter.branch_ids) {
          const n = arr.find(i => i.value === filter.branch_ids);
          if (n) this.branchInput.model = n;
        }
      }
    },
    stages: {
      deep: true,
      immediate: true,
      handler(val) {
        const arr = val.map(i => ({
          name: i.name,
          value: i.id,
          is_admitted: i.is_admitted,
          is_pending: i.is_pending,
          is_discontinued: i.is_discontinued
        }));
        this.stageInput.options = arr;
        /** */
        const filter = this.comparisonGraph.filter_data;
        if (filter.stage_id) {
          const n = arr.find(i => i.value === filter.stage_id);
          if (n) this.stageInput.model = n;
        }
      }
    }
  },
  mounted() {
    const filter = this.comparisonGraph.filter_data;
    if (filter.branch_ids) {
      this.branchInput.model.value = filter.branch_ids;
    }
    if (filter.calculation_method) {
      const n = this.calculationMethodInput.options.find(
        i => i.value === filter.calculation_method
      );
      this.calculationMethodInput.model = n;
    }
    /** */
    if (filter.stage_id) {
      this.stageInput.model.value = filter.stage_id;
    }
    if (filter.group_by) {
      const n = this.groupByInput.options.find(
        i => i.value === filter.group_by
      );
      if (n) this.groupByInput.model = n;
    }

    if (filter.start_date) {
      this.dateInput.model[0].start_date = filter.start_date;
    }
    if (filter.end_date) {
      this.dateInput.model[0].end_date = filter.end_date;
    }

    this.fetchComparisonGraph();
  },
  methods: {
    ...mapActions([
      "actionGet",
      "updateComparisonGraph",
      "deleteComparisonGraph"
    ]),
    onChangeDates(payload) {
      payload.input.model = payload.value;
      this.onUpdate();
      this.fetchComparisonGraph();
    },
    onChangeHandler(payload) {
      payload.input.model = payload.value;
      this.onUpdate();
      this.fetchComparisonGraph();
    },
    fetchComparisonGraph() {
      const start = this.dateInput.model[0].start_date;
      const end = this.dateInput.model[0].end_date;
      const calculation = this.calculationMethodInput.model.value;

      this.groupByInput.options[0].disabled = false;
      this.groupByInput.options[1].disabled = false;
      if (start && end) {
        const diff = moment(end).diff(moment(start), "days") + 1;
        if (diff >= 365) {
          this.groupByInput.options[0].disabled = true;
          this.groupByInput.options[1].disabled = true;
          const groupByDayOrWeek =
            this.groupByInput.model.value === "day" ||
            this.groupByInput.model.value === "week";
          if (groupByDayOrWeek) {
            this.groupByInput.model = { name: "Month", value: "month" };
          }
        }
      }

      let params = `?start_date=${start}&end_date=${end}&calculation_method=${calculation}`;
      if (this.enableBranch) {
        params += `&branch_ids=${this.branchInput.model.value}`;
      }
      if (calculation === "prospects_per_stage") {
        params += `&stage_id=${this.stageInput.model.value}`;
        params += `&group_by=${this.groupByInput.model.value}`;
      }
      if (calculation === "final_outcomes") {
        params += `&group_by=${this.groupByInput.model.value}`;
      }

      const data = {
        url: `/api/v1/analytics/acquisition_comparison${params}`
      };
      this.fetchLoading = true;
      this.graphData = null;
      this.totalProspect = 0;
      this.totalAdmitted = 0;
      this.totalDiscontinued = 0;
      this.actionGet(data).then(({ success, data }) => {
        this.fetchLoading = false;
        if (!success) return;
        this.graphData = data;
        this.generateTotal(data);
      });
    },
    generateTotal(arr) {
      const calculation = this.calculationMethodInput.model.value;
      if (calculation === "prospects_per_stage") {
        this.totalProspect = arr.reduce((n, i) => n + i.total, 0);
      }
      if (calculation === "final_outcomes") {
        this.totalAdmitted = arr.reduce((n, i) => n + i.total_admitted, 0);
        this.totalDiscontinued = arr.reduce(
          (n, i) => n + i.total_discontinued,
          0
        );
      }
      if (calculation === "conversion") {
        this.totalAdmitted = 0;
        const admittedStage = arr.find(i => i.stage_name === "Admitted");
        if (admittedStage) {
          this.totalAdmitted = admittedStage.total;
        }
        this.totalDiscontinued = null;
        const discontinuedStage = arr.find(
          i => i.stage_name === "Discontinued"
        );
        if (discontinuedStage) {
          this.totalDiscontinued = discontinuedStage.total;
        }
      }
    },
    onUpdate() {
      const data = {
        id: this.comparisonGraph.id,
        comparison_graph: {
          filter_data: {
            branch_ids: this.branchInput.model.value,
            calculation_method: this.calculationMethodInput.model.value,
            stage_id: this.stageInput.model.value,
            group_by: this.groupByInput.model.value,
            start_date: this.dateInput.model[0].start_date,
            end_date: this.dateInput.model[0].end_date
          }
        }
      };
      this.updateComparisonGraph(data);
    },
    onRemove() {
      const data = {
        id: this.comparisonGraph.id
      };
      this.deleteLoading = true;
      this.deleteComparisonGraph(data).then(({ success }) => {
        this.deleteLoading = false;
        if (!success) return;
        this.deleteOpened = false;
      });
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/analytics/comparison/AcquisitionGraph.vue